import { Heading, SimpleGrid, GridItem } from '@chakra-ui/react';
import { TFunction } from 'next-i18next';
import dynamic from 'next/dynamic';
import ForestMap from '@/components/common/ForestInformationSection/ForestMap';
import { ForestData } from '@/types/forestData';
import { getForestDisplayData } from '@/lib/forestryData';
import ForestTable from './ForestTable';
import ErrorBoundary from '../ErrorBoundary';
import { ForestPieChartType } from '@/components/common/ForestInformationSection/ForestPieChart';

const ForestPieChart = dynamic(
  () =>
    import('@/components/common/ForestInformationSection/ForestPieChart').then(
      (module) => module.ForestPieChart,
    ),
  {
    // Don't SSR this since it uses ResizeObserver which won't be defined
    ssr: false,
  },
) as ForestPieChartType;

const ForestDataGrid = ({
  forestData,
  t,
}: {
  forestData: ForestData;
  t: TFunction;
}) => {
  const { geoJson } = forestData;

  const {
    treeSpecies,
    mainGroups,
    developmentClasses,
    subGroups,
    fertilityClasses,
  } = getForestDisplayData(forestData, t);

  return (
    <SimpleGrid columns={[1, 1, 2]} spacing={10}>
      <GridItem>
        {treeSpecies.rows.length > 0 && (
          <ForestTable
            mb={10}
            title={t('forest_amount_usable', 'Ainespuun määrä')}
            data={treeSpecies}
            omitColumns={['volume', 'volume_by_area']}
          />
        )}
        {mainGroups.rows.length > 0 && (
          <ForestTable
            title={t('ground_amount', 'Maapohjan määrä')}
            data={mainGroups}
            omitColumns={['volume', 'volume_by_area']}
          />
        )}
      </GridItem>
      {developmentClasses.rows.length > 0 && (
        <GridItem>
          <ForestPieChart
            title={t(
              'forestry_development_class_distribution',
              'Kehitysluokkajakauma',
            )}
            data={developmentClasses}
            valueColumnId="area"
          />
        </GridItem>
      )}
      {subGroups.rows.length > 0 && (
        <GridItem>
          <ForestPieChart
            title={t('forestry_sub_group_distribution', 'Kasvupaikkajakauma')}
            data={subGroups}
            valueColumnId="area"
          />
        </GridItem>
      )}
      {fertilityClasses.rows.length > 0 && (
        <GridItem>
          <ForestPieChart
            title={t(
              'forestry_fertility_class_distribution',
              'Kasvupaikan ravinteisuusjakauma',
            )}
            data={fertilityClasses}
            valueColumnId="area"
          />
        </GridItem>
      )}
      {geoJson && (
        <GridItem colSpan={[1, 1, 2]} className="print-block">
          <Heading size="sm" mb={3}>
            {t('forest_information_on_map', 'Metsävaratiedot kartalla')}
          </Heading>
          <ErrorBoundary message="Tapahtui virhe, metsävaratietokarttaa ei voida näyttää. There was an error displaying the forestry map.">
            <ForestMap geoJson={forestData.geoJson} showUserLocation />
          </ErrorBoundary>
        </GridItem>
      )}
    </SimpleGrid>
  );
};

export default ForestDataGrid;
